import { Body2 } from '@fluentui/react-components';
import { Dispatch, SetStateAction } from 'react';

import type { TFolder, TItem, TItemType, TRootItems } from '@modules/items';
import { MIN_SEARCH_QUERY_LENGTH } from '@modules/search/constants';
import { useSearch } from '@modules/search/api';
import { isTSearchItem } from '@modules/search/types';
import type { TSearchQuery } from '@modules/search/types';
import { StatusLabel } from '@shared/components';
import SelectItemRows from './SelectItemRows';
import { buildSelectableDialogItems } from './selectItemDialogItems';

const SELECT_ITEM_SEARCH_PAGE_SIZE = 25;

type TProps = Pick<TSearchQuery, 'blocksCondition' | 'templatesCondition'> & {
  parent: TFolder | TRootItems;
  query: string;
  folderId?: string;
  itemTypes: TItemType[];
  localSelectedItems: ReadonlyMap<string, TItem>;
  setLocalSelectedItems: Dispatch<SetStateAction<ReadonlyMap<string, TItem>>>;
  checkManageAccess: boolean;
  selectableTypes: TItemType[];
  isSelectable?: (parent: TFolder | TRootItems, item: TItem) => boolean;
  multiSelect: boolean;
  getDisabledReason?: (item: TItem) => string | undefined;
  onPickItem: (item: TItem) => void | Promise<void>;
};

const SelectItemSearchContent = ({
  parent,
  query,
  folderId,
  itemTypes,
  blocksCondition,
  templatesCondition,
  localSelectedItems,
  setLocalSelectedItems,
  checkManageAccess,
  selectableTypes,
  isSelectable,
  multiSelect,
  getDisabledReason,
  onPickItem,
}: TProps) => {
  const trimmedQuery = query.trim();
  const isSearchDisabled = trimmedQuery.length < MIN_SEARCH_QUERY_LENGTH || itemTypes.length === 0;
  const {
    data: searchData,
    error: searchError,
    isLoading: isLoadingSearch,
  } = useSearch(
    {
      query: trimmedQuery,
      pageSize: SELECT_ITEM_SEARCH_PAGE_SIZE,
      folderId,
      itemTypes,
      blocksCondition,
      templatesCondition,
    },
    { isDisabled: isSearchDisabled }
  );

  if (trimmedQuery.length < MIN_SEARCH_QUERY_LENGTH) {
    return <Body2 className="flex justify-center p-4 text-neutral-600">Keep typing to search.</Body2>;
  }

  if (itemTypes.length === 0) {
    return <Body2 className="flex justify-center p-4 text-neutral-600">No matching items found.</Body2>;
  }

  return (
    <StatusLabel
      data={isLoadingSearch ? undefined : searchData}
      loadingMessage="Searching..."
      error={searchError}
      errorMessage="Search failed!"
      render={(data) => {
        const items = buildSelectableDialogItems({
          parent,
          items: data.items,
          selectableTypes,
          checkManageAccess,
          isSelectable,
          getDisabledReason,
        });
        const selectableItems = items.filter((item) => item.isSelectable);

        if (items.length === 0) {
          return <Body2 className="flex justify-center p-4 text-neutral-600">No matching items found.</Body2>;
        }

        const isResultLimitReached = data.total > data.items.length;

        return (
          <>
            <SelectItemRows
              items={items}
              selectableItems={selectableItems}
              localSelectedItems={localSelectedItems}
              setLocalSelectedItems={setLocalSelectedItems}
              isFolderSelectable={false}
              multiSelect={multiSelect}
              onPickItem={onPickItem}
              getDescriptionHtml={(item) => (isTSearchItem(item) ? item.snippet : undefined)}
            />
            {isResultLimitReached && (
              <Body2 className="block p-3 text-center text-neutral-600">
                Showing first {data.items.length} of {data.total}. Refine your search to narrow results.
              </Body2>
            )}
          </>
        );
      }}
    />
  );
};

export default SelectItemSearchContent;
